import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import MatchExplanation from "@/components/MatchExplanation/MatchExplanation";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import {
  createModeratorDealTask,
  deleteModeratorDealFile,
  deleteModeratorDealTask,
  type KybWarning,
  listModeratorContractVersions,
  listModeratorDealFiles,
  listModeratorDealTasks,
  patchModeratorDealTask,
  readKybWarnings,
  unwrapPaginated,
  uploadModeratorDealFile,
} from "@/config/api";
import { formatCurrency, formatDate, formatDateTime } from "@/i18n/format";
import type { Moderator } from "@/services/moderators/generated/model";
import {
  getModeratorsContractsListQueryKey,
  getModeratorsDealsRetrieveQueryKey,
  useModeratorsDealsAssignCreate,
  useModeratorsDealsCommentsCreate,
  useModeratorsDealsContractSaveCreate,
  useModeratorsDealsRetrieve,
  useModeratorsDealsTimelineSaveCreate,
} from "@/services/moderators/generated/moderators-deals/moderators-deals";
import { useModeratorsList } from "@/services/moderators/generated/moderators-moderators/moderators-moderators";
import { getApiErrorMessage } from "../../lib/apiError";
import { notAssessedLabel } from "../../lib/trustLabels";
import "./ModeratorPage.css";
import "./ModeratorDealDetailPage.css";
import "./ModeratorArticlesPage.css";
import DealTradeTermsCard from "@/components/TradeEstimate/DealTradeTermsCard";
import { consentLabel, consentVariant } from "@/lib/supplierConsent";
import {
  buildContractPayload,
  type ContractFormState,
  type ContractStatus,
  emptyContractForm,
} from "../../lib/contractForm";
import DealCoordinatorPanel from "./DealCoordinatorPanel";
import DealKybGateCard from "./DealKybGateCard";
import DealOutcomeCard from "./DealOutcomeCard";

const ModeratorDealDetailPage = () => (
  <ModeratorLayout>
    {(me) => <DealDetailContent isSuper={me.role === "SUPERMODERATOR"} moderatorId={me.id} />}
  </ModeratorLayout>
);

const DealDetailContent = ({ isSuper, moderatorId }: { isSuper: boolean; moderatorId: number }) => {
  const { t } = useTranslation(["deals", "common"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dealId = Number(id);
  const qc = useQueryClient();
  const queryKey = getModeratorsDealsRetrieveQueryKey(dealId);

  const {
    data: deal,
    isLoading,
    error,
  } = useModeratorsDealsRetrieve(dealId, {
    query: { enabled: Number.isFinite(dealId) },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const assignMutation = useModeratorsDealsAssignCreate({ mutation: { onSuccess: invalidate } });
  const commentMutation = useModeratorsDealsCommentsCreate({ mutation: { onSuccess: invalidate } });
  const saveContractMutation = useModeratorsDealsContractSaveCreate({
    mutation: {
      onSuccess: () => {
        invalidate();
        qc.invalidateQueries({ queryKey: getModeratorsContractsListQueryKey() });
        // WP2.3: every save adds a version to the history section.
        qc.invalidateQueries({ queryKey: ["mod-contract-versions", dealId] });
      },
    },
  });
  const saveTimelineMutation = useModeratorsDealsTimelineSaveCreate({
    mutation: { onSuccess: invalidate },
  });

  const { data: modList } = useModeratorsList(undefined, { query: { enabled: isSuper } });
  const moderators = unwrapPaginated<Moderator>(modList).filter((m) => m.isActive);

  const [assignTo, setAssignTo] = useState<string>("");
  const [comment, setComment] = useState("");
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [contractError, setContractError] = useState<string | null>(null);
  const [contractForm, setContractForm] = useState<ContractFormState>(emptyContractForm);
  const [kybWarnings, setKybWarnings] = useState<KybWarning[] | null>(null);

  if (!Number.isFinite(dealId)) {
    return <div className="mod-page__error">{t("dealDetail.invalidId")}</div>;
  }
  if (isLoading) return <div className="mod-page__loading">{t("common:states.loading")}</div>;
  if (error || !deal) return <div className="mod-page__error">{t("dealDetail.loadError")}</div>;

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => navigate(-1)}>
            {t("dealDetail.back")}
          </button>
          <h1 className="mod-page__title">{t("shared.dealTitle", { id: deal.id })}</h1>
          <p className="mod-page__subtitle">
            {deal.requesterCompany?.companyName ?? "—"} ↔ {deal.matchedCompany?.companyName ?? "—"}
          </p>
        </div>
      </header>

      <section className="mod-page__card">
        <h2 className="mod-page__card-title">{t("dealDetail.overview")}</h2>
        <p className="mod-page__card-meta">
          {t("dealDetail.requester")}{" "}
          {deal.requesterCompany ? (
            <Link to={`/moderator/companies/${deal.requesterCompany.id}`}>{deal.requesterCompany.companyName}</Link>
          ) : (
            "—"
          )}
        </p>
        <p className="mod-page__card-meta">
          {t("dealDetail.matched")}{" "}
          {deal.matchedCompany ? (
            <Link to={`/moderator/companies/${deal.matchedCompany.id}`}>{deal.matchedCompany.companyName}</Link>
          ) : (
            "—"
          )}
        </p>
        {deal.matchRequest?.requirementTitle ? (
          <p className="mod-page__card-meta">
            {t("shared.requestLine", { title: deal.matchRequest.requirementTitle })}
          </p>
        ) : null}
        <p className="mod-page__card-meta">
          {t("dealDetail.supplierConsent")}{" "}
          <span className={`mod-page__chip mod-page__chip--${consentVariant(deal.consentStatus)}`}>
            {consentLabel(deal.consentStatus, "buyer")}
          </span>
          {deal.consentRespondedAt ? ` · ${formatDateTime(deal.consentRespondedAt)}` : null}
        </p>
        {deal.consentDeclineReason ? (
          <p className="mod-page__card-meta">{t("dealDetail.declineReason", { reason: deal.consentDeclineReason })}</p>
        ) : null}
        {deal.isManual ? (
          <p className="mod-page__card-meta">
            <span className="mod-page__chip mod-page__chip--warning">{t("shared.manualDeal")}</span>{" "}
            {t("dealDetail.manualReason", { reason: deal.manualReason })}
          </p>
        ) : null}
        <div className="mod-deal-detail__scores">
          <ScoreCell label={t("dealDetail.scores.winWin")} value={deal.winWinMatchScore} highlight />
          <ScoreCell label={t("dealDetail.scores.industry")} value={deal.industryMatch} />
          <ScoreCell label={t("dealDetail.scores.compliance")} value={deal.complianceCert} />
          <ScoreCell label={t("dealDetail.scores.geo")} value={deal.geographicLocation} />
          <ScoreCell label={t("dealDetail.scores.export")} value={deal.exportExperience} />
        </div>
      </section>

      <DealKybGateCard
        dealId={deal.id}
        buyer={{ id: deal.requesterCompany?.id, name: deal.requesterCompany?.companyName }}
        supplier={{ id: deal.matchedCompany?.id, name: deal.matchedCompany?.companyName }}
        hasContract={Boolean(deal.contract)}
        isSuper={isSuper}
        lastWarnings={kybWarnings}
      />

      <DealTradeTermsCard dealId={deal.id} canEdit />

      <DealOutcomeCard key={deal.outcome.updatedAt ?? "new"} deal={deal} onSaved={invalidate} />

      <DealCoordinatorPanel dealId={deal.id} currentModeratorId={moderatorId} isSuper={isSuper} />

      <section className="mod-page__card">
        <MatchExplanation
          lines={deal.explanation}
          confidence={deal.matchConfidence}
          showWeights
          title={t("dealDetail.explanationTitle")}
        />
      </section>

      <section className="mod-page__card">
        <h2 className="mod-page__card-title">{t("dealDetail.assignment.title")}</h2>
        <p className="mod-page__card-meta">
          <Trans
            t={t}
            i18nKey="dealDetail.assignment.current"
            values={{ name: deal.assignedModerator?.fullName ?? t("shared.unassigned") }}
            components={{ strong: <strong /> }}
          />
        </p>
        {isSuper ? (
          <div className="mod-page__form-row">
            <select className="mod-page__select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
              <option value="">{t("shared.selectModerator")}</option>
              {moderators.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName} ({m.email})
                </option>
              ))}
            </select>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              disabled={!assignTo || assignMutation.isPending}
              onClick={() => assignMutation.mutate({ id: deal.id, data: { moderatorId: Number(assignTo) } })}
            >
              {assignMutation.isPending ? t("shared.assigning") : t("shared.assign")}
            </button>
          </div>
        ) : (
          <p className="mod-page__card-meta">{t("dealDetail.assignment.superOnly")}</p>
        )}
      </section>

      <section className="mod-page__card">
        <h2 className="mod-page__card-title">
          {t("dealDetail.comments.title", { count: deal.comments?.length ?? 0 })}
        </h2>
        {(deal.comments ?? []).map((c, i) => (
          <div key={c.id ?? i} className="mod-deal-detail__comment">
            <div className="mod-deal-detail__comment-meta">
              {c.moderator?.fullName ?? t("dealDetail.comments.moderatorFallback")} ·{" "}
              {c.createdAt ? formatDateTime(c.createdAt) : ""}
            </div>
            <div className="mod-deal-detail__comment-body">{c.text}</div>
          </div>
        ))}
        <textarea
          className="mod-page__textarea"
          placeholder={t("dealDetail.comments.placeholder")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <div className="mod-page__form-row">
          <button
            type="button"
            className="mod-page__btn mod-page__btn--primary"
            disabled={!comment.trim() || commentMutation.isPending}
            onClick={() => {
              commentMutation.mutate({ id: deal.id, data: { text: comment.trim() } });
              setComment("");
            }}
          >
            {commentMutation.isPending ? t("shared.posting") : t("shared.postComment")}
          </button>
        </div>
      </section>

      <section className="mod-page__card">
        <h2 className="mod-page__card-title">{t("dealDetail.timeline.title")}</h2>
        <p className="mod-page__card-meta">{t("dealDetail.timeline.hint")}</p>
        <div className="mod-deal-detail__timeline">
          {(deal.timeline ?? []).map((item, idx) => {
            const itemStatus = item.status ?? "pending";
            const itemDate = item.date ?? "";
            return (
              <div key={item.name ?? idx} className="mod-deal-detail__timeline-row">
                <div className="mod-deal-detail__timeline-name">{item.name}</div>
                <select
                  className="mod-page__select"
                  value={itemStatus}
                  onChange={(e) => {
                    const newStatus = e.target.value as "pending" | "done" | "not_done";
                    saveTimelineMutation.mutate({
                      id: deal.id,
                      data: {
                        name: item.name ?? "",
                        status: newStatus,
                        // When moving to "done", default the date to today
                        // unless the moderator has already typed one in.
                        date:
                          newStatus === "done" ? itemDate || new Date().toISOString().slice(0, 10) : itemDate || null,
                      },
                    });
                  }}
                  disabled={saveTimelineMutation.isPending}
                >
                  <option value="pending">{t("dealDetail.timeline.status.pending")}</option>
                  <option value="done">{t("dealDetail.timeline.status.done")}</option>
                  <option value="not_done">{t("dealDetail.timeline.status.not_done")}</option>
                </select>
                <input
                  type="date"
                  className="mod-page__input"
                  value={itemDate.slice(0, 10)}
                  onChange={(e) => {
                    saveTimelineMutation.mutate({
                      id: deal.id,
                      data: {
                        name: item.name ?? "",
                        status: itemStatus as "pending" | "done" | "not_done",
                        date: e.target.value || null,
                      },
                    });
                  }}
                  disabled={saveTimelineMutation.isPending}
                />
              </div>
            );
          })}
        </div>
      </section>

      <section className="mod-page__card">
        <div className="mod-page__card-header">
          <h2 className="mod-page__card-title">{t("dealDetail.contract.title")}</h2>
          <button
            type="button"
            className="mod-page__btn mod-page__btn--primary"
            onClick={() => {
              const c = deal.contract;
              setContractForm(
                c
                  ? {
                      contractType: c.contractType || "",
                      contractValue: c.contractValue ?? "",
                      status: (c.status as ContractStatus) || "draft",
                      startDate: c.startDate ? c.startDate.slice(0, 10) : "",
                      endDate: c.endDate ? c.endDate.slice(0, 10) : "",
                      dateSigned: c.dateSigned ? c.dateSigned.slice(0, 10) : "",
                      description: c.description || "",
                      termsAndConditions: c.termsAndConditions || "",
                      documents: (c.documents || []).join("\n"),
                    }
                  : emptyContractForm,
              );
              setContractError(null);
              setIsContractModalOpen(true);
            }}
          >
            {deal.contract ? t("dealDetail.contract.edit") : t("dealDetail.contract.create")}
          </button>
        </div>
        {deal.contract ? (
          <>
            <p className="mod-page__card-meta">
              {deal.contract.referenceId
                ? t("dealDetail.contract.reference", { reference: deal.contract.referenceId })
                : ""}
              <span className="mod-page__chip mod-page__chip--success">{deal.contract.status}</span>{" "}
              <span className="mod-page__chip">{deal.contract.contractType}</span>
              {deal.contract.contractValue ? (
                <span className="mod-page__chip">{formatCurrency(deal.contract.contractValue)}</span>
              ) : null}
            </p>
            {deal.contract.description ? <p className="mod-page__card-meta">{deal.contract.description}</p> : null}
            {deal.contract.startDate || deal.contract.endDate ? (
              <p className="mod-page__card-meta">
                {deal.contract.startDate ?? "—"} → {deal.contract.endDate ?? "—"}
              </p>
            ) : null}
          </>
        ) : (
          <p className="mod-page__card-meta">{t("dealDetail.contract.none")}</p>
        )}
      </section>

      <ModeratorContractVersions dealId={deal.id} />
      <ModeratorDealTasksSection dealId={deal.id} />
      <ModeratorDealFilesSection dealId={deal.id} />

      {isContractModalOpen && (
        <div className="mod-modal__overlay" onClick={() => setIsContractModalOpen(false)}>
          <div className="mod-modal__content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <header className="mod-modal__header">
              <h2 className="mod-modal__title">
                {deal.contract ? t("dealDetail.contract.edit") : t("dealDetail.contract.new")}
              </h2>
              <button
                type="button"
                className="mod-modal__close"
                onClick={() => setIsContractModalOpen(false)}
                aria-label={t("common:actions.close")}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </header>

            <form
              className="mod-modal__form"
              onSubmit={(e) => {
                e.preventDefault();
                if (!contractForm.contractType.trim()) {
                  setContractError(t("dealDetail.contract.typeRequired"));
                  return;
                }
                const payload = buildContractPayload(contractForm, Boolean(deal.contract));

                saveContractMutation.mutate(
                  { id: deal.id, data: payload },
                  {
                    onSuccess: (saved) => {
                      setIsContractModalOpen(false);
                      setContractError(null);
                      setKybWarnings(readKybWarnings(saved));
                    },
                    onError: (err) => {
                      setContractError(getApiErrorMessage(err, t("dealDetail.contract.saveFailed")));
                      const blocked = readKybWarnings(err);
                      if (blocked.length > 0) setKybWarnings(blocked);
                    },
                  },
                );
              }}
            >
              <div className="mod-modal__row">
                <label className="mod-modal__label">
                  {t("dealDetail.contract.fields.type")}
                  <input
                    type="text"
                    className="mod-page__input"
                    value={contractForm.contractType}
                    onChange={(e) => setContractForm({ ...contractForm, contractType: e.target.value })}
                    required
                  />
                </label>
                <label className="mod-modal__label">
                  {t("dealDetail.contract.fields.status")}
                  <select
                    className="mod-page__select"
                    value={contractForm.status}
                    onChange={(e) => setContractForm({ ...contractForm, status: e.target.value as ContractStatus })}
                  >
                    <option value="draft">{t("shared.contractStatus.draft")}</option>
                    <option value="active">{t("shared.contractStatus.active")}</option>
                    <option value="completed">{t("shared.contractStatus.completed")}</option>
                    <option value="cancelled">{t("shared.contractStatus.cancelled")}</option>
                  </select>
                </label>
              </div>

              <div className="mod-modal__row">
                <label className="mod-modal__label">
                  {t("dealDetail.contract.fields.value")}
                  <input
                    type="number"
                    step="0.01"
                    className="mod-page__input"
                    value={contractForm.contractValue}
                    onChange={(e) => setContractForm({ ...contractForm, contractValue: e.target.value })}
                  />
                </label>
                <label className="mod-modal__label">
                  {t("dealDetail.contract.fields.dateSigned")}
                  <input
                    type="date"
                    className="mod-page__input"
                    value={contractForm.dateSigned}
                    onChange={(e) => setContractForm({ ...contractForm, dateSigned: e.target.value })}
                  />
                </label>
              </div>

              <div className="mod-modal__row">
                <label className="mod-modal__label">
                  {t("dealDetail.contract.fields.startDate")}
                  <input
                    type="date"
                    className="mod-page__input"
                    value={contractForm.startDate}
                    onChange={(e) => setContractForm({ ...contractForm, startDate: e.target.value })}
                  />
                </label>
                <label className="mod-modal__label">
                  {t("dealDetail.contract.fields.endDate")}
                  <input
                    type="date"
                    className="mod-page__input"
                    value={contractForm.endDate}
                    onChange={(e) => setContractForm({ ...contractForm, endDate: e.target.value })}
                  />
                </label>
              </div>

              <label className="mod-modal__label">
                {t("dealDetail.contract.fields.description")}
                <textarea
                  className="mod-page__input"
                  rows={3}
                  value={contractForm.description}
                  onChange={(e) => setContractForm({ ...contractForm, description: e.target.value })}
                />
              </label>

              <label className="mod-modal__label">
                {t("dealDetail.contract.fields.terms")}
                <textarea
                  className="mod-page__input"
                  rows={4}
                  value={contractForm.termsAndConditions}
                  onChange={(e) => setContractForm({ ...contractForm, termsAndConditions: e.target.value })}
                />
              </label>

              <label className="mod-modal__label">
                {t("dealDetail.contract.fields.documents")}
                <textarea
                  className="mod-page__input"
                  rows={3}
                  placeholder="https://s3.example/contract.pdf"
                  value={contractForm.documents}
                  onChange={(e) => setContractForm({ ...contractForm, documents: e.target.value })}
                />
                <small>{t("dealDetail.contract.fields.documentsHint")}</small>
              </label>

              {contractError ? <p className="mod-modal__error">{contractError}</p> : null}

              <div className="mod-modal__footer">
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--ghost"
                  onClick={() => setIsContractModalOpen(false)}
                >
                  {t("common:actions.cancel")}
                </button>
                <button
                  type="submit"
                  className="mod-page__btn mod-page__btn--primary"
                  disabled={saveContractMutation.isPending}
                >
                  {saveContractMutation.isPending ? t("common:actions.saving") : t("common:actions.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const ScoreCell = ({ label, value, highlight }: { label: string; value: number | null; highlight?: boolean }) => (
  <div className={`mod-deal-detail__score ${highlight ? "mod-deal-detail__score--highlight" : ""}`}>
    <div className="mod-deal-detail__score-label">{label}</div>
    <div className="mod-deal-detail__score-value">{value ?? notAssessedLabel()}</div>
  </div>
);

// --------------------------------------------------------------------------
// WP2.3 — moderator sees contract version history, task CRUD, and files.
// Companies see the current contract version via `/b2b/deals/:id`.
// --------------------------------------------------------------------------

const ModeratorContractVersions = ({ dealId }: { dealId: number }) => {
  const { t } = useTranslation(["deals"]);
  const { data, isLoading, error } = useQuery({
    queryKey: ["mod-contract-versions", dealId],
    queryFn: () => listModeratorContractVersions(dealId),
    retry: false,
  });
  if (isLoading) return null;
  if (error) return null;
  const rows = data ?? [];
  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("workspace.contract.versionsTitle")}</h2>
      {rows.length === 0 ? (
        <p className="mod-page__card-meta">{t("workspace.contract.none")}</p>
      ) : (
        <ul className="mod-page__list">
          {rows.map((v) => (
            <li key={v.versionNumber} className="mod-page__list-item">
              <strong>
                {t("workspace.contract.versionRow", { version: v.versionNumber, date: formatDateTime(v.savedAt) })}
              </strong>
              {v.savedByModeratorName && <> · {t("workspace.contract.versionBy", { name: v.savedByModeratorName })}</>}
              <div className="mod-page__card-meta">
                {v.status} · {v.contractType}
                {v.contractValue ? ` · ${v.contractValue}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const ModeratorDealTasksSection = ({ dealId }: { dealId: number }) => {
  const { t } = useTranslation(["deals", "common"]);
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ title: "", description: "", dueDate: "" });
  const [formError, setFormError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["mod-deal-tasks", dealId],
    queryFn: () => listModeratorDealTasks(dealId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["mod-deal-tasks", dealId] });
    qc.invalidateQueries({ queryKey: ["deal-workspace-tasks", dealId] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createModeratorDealTask(dealId, {
        title: draft.title,
        description: draft.description,
        dueDate: draft.dueDate || null,
      }),
    onSuccess: () => {
      setDraft({ title: "", description: "", dueDate: "" });
      setShowForm(false);
      setFormError(null);
      invalidate();
    },
    onError: (err) => setFormError(getApiErrorMessage(err, t("workspace.errors.saveTask"))),
  });

  const flipMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "open" | "done" }) =>
      patchModeratorDealTask(dealId, id, { status }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteModeratorDealTask(dealId, id),
    onSuccess: invalidate,
  });

  const rows = data ?? [];
  return (
    <section className="mod-page__card">
      <div className="mod-page__card-header">
        <h2 className="mod-page__card-title">{t("workspace.tasks.title")}</h2>
        <button type="button" className="mod-page__btn" onClick={() => setShowForm((v) => !v)}>
          {t("workspace.tasks.newTask")}
        </button>
      </div>
      <p className="mod-page__card-meta">{t("workspace.tasks.hint")}</p>
      {showForm && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.5rem" }}>
          <input
            type="text"
            placeholder={t("workspace.tasks.titlePlaceholder")}
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
          <textarea
            placeholder={t("workspace.tasks.descriptionPlaceholder")}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
          <input type="date" value={draft.dueDate} onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })} />
          {formError && <div className="mod-page__error">{formError}</div>}
          <div>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              onClick={() => createMutation.mutate()}
              disabled={!draft.title.trim() || !draft.dueDate || createMutation.isPending}
            >
              {createMutation.isPending ? t("workspace.tasks.savingTask") : t("workspace.tasks.save")}
            </button>
            <button type="button" className="mod-page__btn" onClick={() => setShowForm(false)}>
              {t("workspace.tasks.cancel")}
            </button>
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <p className="mod-page__card-meta">{t("workspace.tasks.empty")}</p>
      ) : (
        <ul className="mod-page__list">
          {rows.map((task) => (
            <li key={task.id} className="mod-page__list-item">
              <strong>{task.title}</strong>
              <div className="mod-page__card-meta">
                {task.dueDate
                  ? t("workspace.tasks.due", { date: formatDate(task.dueDate) })
                  : t("workspace.tasks.noDueDate")}
                {" · "}
                {task.status === "done" ? t("workspace.tasks.statusDone") : t("workspace.tasks.statusOpen")}
                {task.assigneeName ? ` · ${t("workspace.tasks.assignee", { name: task.assigneeName })}` : ""}
              </div>
              {task.description && <div className="mod-page__card-meta">{task.description}</div>}
              <button
                type="button"
                className="mod-page__btn"
                onClick={() => flipMutation.mutate({ id: task.id, status: task.status === "done" ? "open" : "done" })}
              >
                {task.status === "done" ? t("workspace.tasks.reopen") : t("workspace.tasks.markDone")}
              </button>
              <button
                type="button"
                className="mod-page__btn"
                onClick={() => {
                  if (window.confirm(t("workspace.tasks.confirmDelete"))) deleteMutation.mutate(task.id);
                }}
              >
                {t("workspace.tasks.delete")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const ModeratorDealFilesSection = ({ dealId }: { dealId: number }) => {
  const { t } = useTranslation(["deals", "common"]);
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ["mod-deal-files", dealId],
    queryFn: () => listModeratorDealFiles(dealId),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["mod-deal-files", dealId] });
    qc.invalidateQueries({ queryKey: ["deal-workspace-files", dealId] });
  };
  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadModeratorDealFile(dealId, file),
    onSuccess: () => {
      setUploadError(null);
      if (fileInput.current) fileInput.current.value = "";
      invalidate();
    },
    onError: (err) => setUploadError(getApiErrorMessage(err, t("workspace.errors.uploadFile"))),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteModeratorDealFile(dealId, id),
    onSuccess: invalidate,
  });

  const rows = data ?? [];
  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("workspace.files.title")}</h2>
      <p className="mod-page__card-meta">{t("workspace.files.hint")}</p>
      <input
        ref={fileInput}
        type="file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) uploadMutation.mutate(file);
        }}
        disabled={uploadMutation.isPending}
      />
      {uploadError && <div className="mod-page__error">{uploadError}</div>}
      {rows.length === 0 ? (
        <p className="mod-page__card-meta">{t("workspace.files.empty")}</p>
      ) : (
        <ul className="mod-page__list">
          {rows.map((f) => (
            <li key={f.id} className="mod-page__list-item">
              <strong>{f.fileName}</strong>
              <div className="mod-page__card-meta">
                {f.uploadedBy.side === "moderator"
                  ? t("workspace.files.uploadedByModerator", { name: f.uploadedBy.name })
                  : t("workspace.files.uploadedBy", { name: f.uploadedBy.name })}
                {" · "}
                {formatDateTime(f.createdAt)} · {t("workspace.files.size", { size: f.size })}
              </div>
              <a href={f.url} target="_blank" rel="noreferrer" className="mod-page__btn">
                {t("workspace.files.download")}
              </a>
              <button
                type="button"
                className="mod-page__btn"
                onClick={() => {
                  if (window.confirm(t("workspace.files.confirmDelete"))) deleteMutation.mutate(f.id);
                }}
              >
                {t("workspace.files.delete")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ModeratorDealDetailPage;
